import type { MouseEvent, ReactElement, ReactNode, Ref } from "react";
import { FRAMES } from "odyssey-shared";
import { BusyDots } from "./BusyDots.tsx";
import { CentredRow } from "./Centred.tsx";
import { classes } from "./classes.ts";
import { Glyph, type GlyphName } from "./Glyph.tsx";
import { Icon, type IconName } from "./Icon.tsx";
import { useInteraction } from "./interaction.tsx";
import { GLYPH_SIZES, KIT_METRICS, buttonGlyphTop, centreOffset, logical } from "./metrics.ts";
import { AT_REST, buttonFrame, frameClass, type ButtonVariant, type Interaction } from "./state.ts";

/**
 * Buttons (brief section 5.1): Button in its primary, secondary and danger variants, IconButton and
 * TextLink. Each draws its frame for its state from the state tables, takes pointer events although the
 * overlay root does not, shows keyboard focus as the aether outline, and keeps its size when busy: the
 * label stays in place, hidden, under three blinking dots. A busy button cannot be activated but stays
 * focusable, with its focus ring: it is not disabled, so focus does not drop out of a form while it waits,
 * and it refuses its clicks instead. As the design draws them (ADR-061's amendment "the kit and the entry forms
 * match the design"), a button may lead with a glyph and span its container, and an icon button may carry a
 * badge.
 */

/**
 * The state classes a control carries, which the stylesheet reads for its text colour, its focus outline
 * and its pressed offset.
 *
 * @param state The interaction.
 * @param own The control's own states.
 * @returns The class names.
 */
function stateClasses(state: Interaction, own: { readonly disabled: boolean; readonly selected?: boolean; readonly busy?: boolean }): string {
  return classes(state.hover && "is-hover", state.pressed && "is-pressed", state.focus && "is-focus", own.disabled && "is-disabled", own.selected === true && "is-selected", own.busy === true && "is-busy");
}

/**
 * What a busy control shows of its interaction: keyboard focus, so its focus ring stays, but no hover and
 * no press, since it cannot be activated.
 *
 * @param state The interaction.
 * @param busy Whether the control waits.
 * @returns The interaction to draw.
 */
function whileBusy(state: Interaction, busy: boolean): Interaction {
  return busy ? { ...AT_REST, focus: state.focus } : state;
}

/**
 * Cancels a busy button's activation. A prevented click neither runs the page's handler nor submits the
 * button's form, and a form's implicit submission (Enter in one of its fields) is a click on its default
 * button, so this also stops the form being submitted again while it waits.
 *
 * @param event The click.
 */
function refuse(event: MouseEvent<HTMLButtonElement>): void {
  event.preventDefault();
}

/**
 * A button's label, led by its glyph when it has one, or followed by the 8 px caret of a button that opens a menu,
 * set in the middle of the label's line.
 *
 * @param props The label, the glyph, and whether the caret follows it.
 * @returns The label.
 */
function ButtonLabel({ children, icon, caret = false }: { readonly children: ReactNode; readonly icon: GlyphName | undefined; readonly caret?: boolean }): ReactElement {
  if (caret) {
    return (
      <span className="ot-button__label ot-button__label--glyph" style={{ gap: logical(KIT_METRICS.buttonGlyph.caretGap) }}>
        <span>{children}</span>
        <span className="ot-button__glyph" style={{ marginTop: logical(buttonGlyphTop(GLYPH_SIZES["chevron-down"])) }}>
          <Glyph name="chevron-down" />
        </span>
      </span>
    );
  }
  if (icon === undefined) {
    return <span className="ot-button__label">{children}</span>;
  }
  return (
    <span className="ot-button__label ot-button__label--glyph" style={{ gap: logical(KIT_METRICS.buttonGlyph.gap) }}>
      <span className="ot-button__glyph" style={{ marginTop: logical(buttonGlyphTop(GLYPH_SIZES[icon])) }}>
        <Glyph name={icon} />
      </span>
      <span>{children}</span>
    </span>
  );
}

/**
 * A button with a pixel-font label.
 *
 * @param props The label; the variant (secondary unless given); `onClick`; `disabled`; `busy`, which
 *   refuses every activation (a click, Space, Enter, and Enter in a field of its form) and shows the dots
 *   at the same size; `selected` for a secondary button that toggles, which the types refuse on a primary
 *   or a danger button (one leads a surface, the other destroys; neither toggles) and the markup ignores
 *   there; `type` for a form's submit button; `icon`, a glyph set before the label in the middle of its line;
 *   `fullWidth`, which makes the button as wide as its container with its label in the middle; `busyLabel`,
 *   the words that name the button while it is busy (such as "Logging in"), since its label is hidden then; and
 *   `label`, the words that name a button whose visible text is a symbol (such as "Next outfit" on a ">"); and
 *   `menuOpen` for a button that opens a menu (the design's Filter and Sort, ADR-125 decision 4.2): given, the label
 *   is followed by the 8 px caret and the button says it opens a menu and whether that menu is open, and its
 *   `selected` look is drawn without saying it is pressed; and `describedBy`, the id of an element that describes it.
 * @returns The button.
 * @example
 * <Button variant="primary" icon="reload" onClick={retry}>Retry</Button>
 */
export function Button({ children, variant = "secondary", onClick, disabled = false, busy = false, busyLabel, label, selected, type = "button", icon, fullWidth = false, menuOpen, describedBy }: { readonly children: ReactNode; readonly label?: string; readonly menuOpen?: boolean; readonly describedBy?: string | undefined; readonly onClick?: (event: MouseEvent<HTMLButtonElement>) => void; readonly disabled?: boolean; readonly busy?: boolean; readonly busyLabel?: string; readonly type?: "button" | "submit"; readonly icon?: GlyphName; readonly fullWidth?: boolean } & ({ readonly variant?: "secondary"; readonly selected?: boolean } | { readonly variant: Exclude<ButtonVariant, "secondary">; readonly selected?: never })): ReactElement {
  const { state, handlers } = useInteraction({ disabled, focus: "visible" });
  const shown = whileBusy(state, busy);
  const toggles = variant === "secondary";
  const toggled = toggles && selected === true;
  const frame = buttonFrame(variant, { ...shown, disabled, selected: toggled });
  return (
    <button
      type={type}
      className={classes("ot-button", `ot-button--${variant}`, fullWidth && "ot-button--full", frameClass(frame), stateClasses(shown, { disabled, selected: toggled, busy }))}
      data-kit="button"
      data-ot-danger={variant === "danger" ? "" : undefined}
      disabled={disabled}
      aria-label={busy && busyLabel !== undefined ? busyLabel : label}
      aria-describedby={describedBy}
      aria-busy={busy ? true : undefined}
      aria-disabled={busy ? true : undefined}
      aria-pressed={toggles && menuOpen === undefined ? selected : undefined}
      aria-haspopup={menuOpen === undefined ? undefined : "menu"}
      aria-expanded={menuOpen}
      onClick={busy ? refuse : onClick}
      style={{ height: logical(KIT_METRICS.button.height), paddingLeft: logical(KIT_METRICS.button.paddingX), paddingRight: logical(KIT_METRICS.button.paddingX) }}
      {...handlers}
    >
      {fullWidth ? (
        <CentredRow inline>
          <ButtonLabel icon={icon} caret={menuOpen !== undefined}>
            {children}
          </ButtonLabel>
        </CentredRow>
      ) : (
        <ButtonLabel icon={icon} caret={menuOpen !== undefined}>
          {children}
        </ButtonLabel>
      )}
      {busy ? <BusyDots top={KIT_METRICS.busy.buttonTop} /> : null}
    </button>
  );
}

/**
 * A drawn icon for an icon button: the image and its side in logical pixels (one image pixel to one logical pixel),
 * centred in a button of that side with no frame or face, in place of a glyph; the button's tap target is at least 44 CSS
 * pixels square.
 */
export interface IconArt {
  readonly src: string;
  readonly size: number;
}

/**
 * A square button showing one icon at the button role's size, named for assistive technology by its label,
 * which is also why it needs no tooltip: nothing about it is shown only on hover. A badge, such as a count of
 * unread lines, sits over its top-right corner in the pixel face on the danger colour; assistive technology
 * reads it after the label, since the button's name carries both.
 *
 * @param props The icon, or a kit `glyph` instead where the set has no icon for it; the label that names the action; `onClick`; `disabled`; `busy`, which refuses
 *   every activation as a busy `Button` does; `selected` for an icon button that toggles; and `badge`, a short
 *   count or word.
 * @returns The button.
 * @example
 * <IconButton icon="menu" label="Menu" badge={3} onClick={open} />
 */
export function IconButton({ icon, glyph, art, label, onClick, disabled = false, busy = false, selected, badge }: { readonly icon?: IconName; readonly glyph?: GlyphName; readonly art?: IconArt; readonly label: string; readonly onClick?: (event: MouseEvent<HTMLButtonElement>) => void; readonly disabled?: boolean; readonly busy?: boolean; readonly selected?: boolean; readonly badge?: string | number }): ReactElement {
  const { state, handlers } = useInteraction({ disabled, focus: "visible" });
  const shown = whileBusy(state, busy);
  const frame = buttonFrame("secondary", { ...shown, disabled, selected: selected === true });
  return (
    <button
      type="button"
      className={classes("ot-button", "ot-button--icon", art === undefined ? frameClass(frame) : "ot-button--bare", stateClasses(shown, { disabled, selected: selected === true, busy }))}
      data-kit="icon-button"
      disabled={disabled}
      aria-label={badge === undefined ? label : `${label}, ${badge}`}
      aria-busy={busy ? true : undefined}
      aria-disabled={busy ? true : undefined}
      aria-pressed={selected}
      onClick={busy ? refuse : onClick}
      style={{ width: logical(art === undefined ? KIT_METRICS.iconButton.width : art.size), height: logical(art === undefined ? KIT_METRICS.iconButton.height : art.size) }}
      {...handlers}
    >
      {art === undefined ? (
        <span className="ot-button__label">{glyph === undefined ? <Icon name={icon ?? "menu"} role="button" /> : <Glyph name={glyph} />}</span>
      ) : (
        <span className="ot-button__label ot-button__label--art" data-kit="icon-button-art">
          <img className="ot-button__art" src={art.src} alt="" draggable={false} style={{ width: logical(art.size), height: logical(art.size) }} />
        </span>
      )}
      {busy ? <BusyDots top={KIT_METRICS.busy.iconTop} left={KIT_METRICS.busy.iconLeft} /> : null}
      {badge === undefined ? null : (
        <span className="ot-badge" data-kit="badge" aria-hidden="true" style={{ height: logical(KIT_METRICS.badge.height), minWidth: logical(KIT_METRICS.badge.height), paddingLeft: logical(KIT_METRICS.badge.paddingX), paddingRight: logical(KIT_METRICS.badge.paddingX), top: logical(-KIT_METRICS.badge.offset), right: logical(-KIT_METRICS.badge.offset) }}>
          {badge}
        </span>
      )}
    </button>
  );
}

/**
 * Where a glyph sits inside a square button, from the inside of the button frame's edge: the glyph centred in the
 * button's whole side, on whole logical pixels.
 *
 * @param size The button's side in logical pixels.
 * @param glyph The glyph's side in logical pixels.
 * @returns The glyph's left and top.
 * @throws RangeError when either size is not a whole number.
 * @example
 * glyphButtonOffset(16, 12);
 * // { left: 1, top: 0 }
 */
export function glyphButtonOffset(size: number, glyph: number): { readonly left: number; readonly top: number } {
  const centre = centreOffset(size, glyph);
  return { left: centre - FRAMES.button.insets.left, top: centre - FRAMES.button.insets.top };
}

/**
 * Keeps focus where it was when a control is pressed with the pointer; the click still acts.
 *
 * @param event The press.
 */
function keepFocusOn(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * A small square button showing one of the kit's glyphs at the glyph's own size (the design's `IconButton` at its
 * 10, 12 and 16 sizes, a visual reference), named for assistive technology by its label, so nothing about it is shown
 * only on hover. The glyph is set in the middle of the button and moves down a pixel while the button is pressed.
 *
 * @param props The `glyph`; the `label` that names the action; the button's side in logical px (`size`); `onClick`;
 *   `disabled`; `type` for a form's submit button; `keepFocus`, which leaves focus where it was when the pointer
 *   presses the button, so a field keeps the keyboard; `expanded` and `controls` for a button that shows and hides
 *   something; `buttonRef`, where the caller keeps the element to focus it; and `data` attributes to find it by.
 * @returns The button.
 * @example
 * <GlyphButton glyph="arrow-right" label="Send" size={16} type="submit" />
 */
export function GlyphButton({ glyph, label, size, onClick, disabled = false, type = "button", keepFocus = false, expanded, controls, buttonRef, data }: { readonly glyph: GlyphName; readonly label: string; readonly size: number; readonly onClick?: (event: MouseEvent<HTMLButtonElement>) => void; readonly disabled?: boolean; readonly type?: "button" | "submit"; readonly keepFocus?: boolean; readonly expanded?: boolean | undefined; readonly controls?: string | undefined; readonly buttonRef?: Ref<HTMLButtonElement> | undefined; readonly data?: Readonly<Record<`data-${string}`, string>> | undefined }): ReactElement {
  const { state, handlers } = useInteraction({ disabled, focus: "visible" });
  const frame = buttonFrame("secondary", { ...state, disabled, selected: false });
  const offset = glyphButtonOffset(size, GLYPH_SIZES[glyph]);
  return (
    <button
      ref={buttonRef}
      type={type}
      className={classes("ot-button", "ot-button--glyph", frameClass(frame), stateClasses(state, { disabled }))}
      data-kit="glyph-button"
      disabled={disabled}
      aria-label={label}
      aria-expanded={expanded}
      aria-controls={controls}
      onClick={onClick}
      onMouseDown={keepFocus ? keepFocusOn : undefined}
      style={{ width: logical(size), height: logical(size) }}
      {...handlers}
      {...data}
    >
      <span className="ot-button__mark" style={{ left: logical(offset.left), top: logical(offset.top + (state.pressed ? 1 : 0)) }}>
        <Glyph name={glyph} />
      </span>
    </button>
  );
}

/**
 * A text link in the pixel font, such as the one that switches a form to creating an account: a button
 * unless it goes to an address. A disabled link with an address drops the address and its click, so it is
 * neither in the tab order nor followed by a click or Enter, and keeps the link role with `aria-disabled`.
 *
 * @param props The label; `onClick`, or `href` for a link to an address; and `disabled`.
 * @returns The link.
 */
export function TextLink({ children, onClick, href, disabled = false }: { readonly children: ReactNode; readonly onClick?: (event: MouseEvent<HTMLElement>) => void; readonly href?: string; readonly disabled?: boolean }): ReactElement {
  const { state, handlers } = useInteraction({ disabled, focus: "visible" });
  const className = classes("ot-link", stateClasses(state, { disabled }));
  if (href !== undefined && disabled) {
    return (
      <a className={className} data-kit="link" role="link" aria-disabled="true" {...handlers}>
        {children}
      </a>
    );
  }
  if (href !== undefined) {
    return (
      <a className={className} data-kit="link" href={href} onClick={onClick} {...handlers}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={className} data-kit="link" disabled={disabled} onClick={onClick} {...handlers}>
      {children}
    </button>
  );
}
